import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import type { Quote, ModalityType, OperationType, CargoType, VolumeItem } from '../types/quote'

export interface QuoteExportData extends Quote {
  shipper_name?: string
  shipper_trading_name?: string
  shipper_cnpj?: string
  shipper_logo_url?: string
  goods_ncm?: string
  goods_ncm_list?: string[]
}

const MODALITY_LABELS: Record<ModalityType, string> = {
  maritimo: 'Marítimo',
  aereo: 'Aéreo',
  terrestre: 'Terrestre'
}

const OPERATION_LABELS: Record<OperationType, string> = {
  import: 'Importação',
  export: 'Exportação'
}

const CARGO_TYPE_LABELS: Record<CargoType, string> = {
  geral: 'Carga Geral',
  empilhavel: 'Empilhável',
  perigosa: 'Perigosa'
}

function formatDate(dateString: string): string {
  try {
    return new Date(dateString).toLocaleDateString('pt-BR')
  } catch {
    return dateString
  }
}

function formatLocation(location: { city: string; state?: string; country: string }): string {
  const parts = [location.city]
  if (location.state) parts.push(location.state)
  parts.push(location.country)
  return parts.join(', ')
}

function formatWeight(weight: number): string {
  if (weight >= 1000) {
    return `${(weight / 1000).toFixed(2)} ton`
  }
  return `${weight} kg`
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

/**
 * Fetch image and convert to base64 data URL
 */
async function imageToBase64(url: string): Promise<string | null> {
  if (!url) return null

  try {
    const response = await fetch(url, { mode: 'cors' })
    if (!response.ok) return null

    const blob = await response.blob()
    if (!blob.type.startsWith('image/')) return null

    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch (err) {
    console.warn('[PDF] Failed to load logo:', err)
    return null
  }
}

/**
 * Create HTML content for PDF
 */
function createPdfHtml(quote: QuoteExportData, logoBase64: string | null): string {
  const initials = getInitials(quote.shipper_trading_name || quote.shipper_name || 'E')

  const logoHtml = logoBase64
    ? `<img src="${logoBase64}" style="width: 60px; height: 60px; border-radius: 8px; object-fit: cover;" />`
    : `<div style="width: 60px; height: 60px; background: linear-gradient(135deg, #059669, #047857); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px; font-weight: bold;">${initials}</div>`

  const modalityInfo = []
  if (quote.modality_name) {
    modalityInfo.push(MODALITY_LABELS[quote.modality_name] || quote.modality_name)
  }
  if (quote.modality_type) {
    modalityInfo.push(OPERATION_LABELS[quote.modality_type] || quote.modality_type)
  }
  if (quote.volume_name) {
    modalityInfo.push(quote.volume_name)
  }
  modalityInfo.push(quote.incoterm)

  let volumesHtml = ''
  if (quote.volume_items && quote.volume_items.length > 0) {
    const totalQty = quote.volume_items.reduce((sum, item) => sum + item.quantity, 0)
    const totalWeight = quote.volume_items.reduce((sum, item) => sum + (item.weight * item.quantity), 0)

    volumesHtml = `
      <div style="margin-top: 16px;">
        <div style="font-size: 11px; color: #6B7280; margin-bottom: 8px; font-weight: 600;">VOLUMES</div>
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
          <thead>
            <tr style="background: #F3F4F6;">
              <th style="text-align: left; padding: 8px; border-bottom: 1px solid #E5E7EB;">Tipo</th>
              <th style="text-align: center; padding: 8px; border-bottom: 1px solid #E5E7EB;">Qtd</th>
              <th style="text-align: right; padding: 8px; border-bottom: 1px solid #E5E7EB;">Peso</th>
              <th style="text-align: center; padding: 8px; border-bottom: 1px solid #E5E7EB;">Dimensões</th>
              <th style="text-align: left; padding: 8px; border-bottom: 1px solid #E5E7EB;">NCM</th>
            </tr>
          </thead>
          <tbody>
            ${quote.volume_items.map((item: VolumeItem) => `
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #F3F4F6;">${item.type}</td>
                <td style="text-align: center; padding: 8px; border-bottom: 1px solid #F3F4F6;">${item.quantity}</td>
                <td style="text-align: right; padding: 8px; border-bottom: 1px solid #F3F4F6;">${formatWeight(item.weight)}</td>
                <td style="text-align: center; padding: 8px; border-bottom: 1px solid #F3F4F6;">${
                  item.dimensions
                    ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height} ${item.dimensions.unit}`
                    : '-'
                }</td>
                <td style="padding: 8px; border-bottom: 1px solid #F3F4F6; font-family: monospace; font-size: 10px;">${item.ncm || '-'}</td>
              </tr>
            `).join('')}
            <tr style="background: #F3F4F6; font-weight: 600;">
              <td style="padding: 8px;">Total</td>
              <td style="text-align: center; padding: 8px;">${totalQty}</td>
              <td style="text-align: right; padding: 8px;">${formatWeight(totalWeight)}</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    `
  }

  return `
    <div style="font-family: 'Segoe UI', Roboto, sans-serif; padding: 40px; width: 595px; min-height: 842px; box-sizing: border-box; background: white;">
      <!-- Header -->
      <div style="display: flex; align-items: flex-start; margin-bottom: 20px;">
        <div style="margin-right: 16px;">
          ${logoHtml}
        </div>
        <div style="flex: 1;">
          <div style="font-size: 16px; font-weight: bold; color: #111827;">
            ${quote.shipper_trading_name || quote.shipper_name || 'Empresa'}
          </div>
          ${quote.shipper_trading_name && quote.shipper_name ? `
            <div style="font-size: 12px; color: #6B7280; margin-top: 2px;">${quote.shipper_name}</div>
          ` : ''}
          ${quote.shipper_cnpj ? `
            <div style="font-size: 12px; color: #6B7280; margin-top: 2px;">CNPJ: ${quote.shipper_cnpj}</div>
          ` : ''}
        </div>
        <div style="text-align: right;">
          <div style="font-size: 11px; color: #6B7280;">COTAÇÃO DE FRETE</div>
          <div style="font-size: 20px; font-weight: bold; color: #059669; margin-top: 4px;">
            ${quote.tx_control || `#${quote.id.slice(0, 8)}`}
          </div>
          ${quote.client_reference ? `
            <div style="font-size: 12px; color: #374151; margin-top: 4px;">
              Ref: ${quote.client_reference}
            </div>
          ` : ''}
          <div style="font-size: 12px; color: #374151; margin-top: 4px;">
            ${formatDate(quote.created_at)}
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div style="border-bottom: 1px solid #E5E7EB; margin-bottom: 20px;"></div>

      <!-- Route Section -->
      <div style="margin-bottom: 24px;">
        <div style="font-size: 14px; font-weight: bold; color: #374151; margin-bottom: 12px;">ROTA</div>
        <div style="display: flex; gap: 24px;">
          <div style="flex: 1; background: #F9FAFB; padding: 12px; border-radius: 8px;">
            <div style="font-size: 10px; color: #6B7280; margin-bottom: 4px;">Origem</div>
            <div style="font-size: 13px; font-weight: 600; color: #111827;">${formatLocation(quote.origin.location)}</div>
            ${quote.origin.collect_date ? `
              <div style="font-size: 11px; color: #6B7280; margin-top: 4px;">Coleta: ${formatDate(quote.origin.collect_date)}</div>
            ` : ''}
          </div>
          <div style="flex: 1; background: #F9FAFB; padding: 12px; border-radius: 8px;">
            <div style="font-size: 10px; color: #6B7280; margin-bottom: 4px;">Destino</div>
            <div style="font-size: 13px; font-weight: 600; color: #111827;">${formatLocation(quote.destination.location)}</div>
            ${quote.destination.delivery_date ? `
              <div style="font-size: 11px; color: #6B7280; margin-top: 4px;">Entrega: ${formatDate(quote.destination.delivery_date)}</div>
            ` : ''}
          </div>
        </div>
      </div>

      <!-- Modality Section -->
      <div style="margin-bottom: 24px;">
        <div style="font-size: 14px; font-weight: bold; color: #374151; margin-bottom: 12px;">MODALIDADE</div>
        <div style="display: flex; gap: 8px; flex-wrap: wrap;">
          ${modalityInfo.map(item => `
            <div style="background: #ECFDF5; color: #059669; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; border: 1px solid #D1FAE5;">
              ${item}
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Cargo Section -->
      <div style="margin-bottom: 24px;">
        <div style="font-size: 14px; font-weight: bold; color: #374151; margin-bottom: 12px;">CARGA</div>
        ${quote.goods_description ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 10px; color: #9CA3AF;">Descrição: </span>
            <span style="font-size: 12px; color: #111827;">${quote.goods_description}</span>
          </div>
        ` : ''}
        ${(quote.goods_ncm_list && quote.goods_ncm_list.length > 0) ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 10px; color: #9CA3AF;">NCM: </span>
            <span style="font-size: 12px; color: #111827; font-family: monospace;">${quote.goods_ncm_list.join(', ')}</span>
          </div>
        ` : (quote.goods_ncm ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 10px; color: #9CA3AF;">NCM: </span>
            <span style="font-size: 12px; color: #111827; font-family: monospace;">${quote.goods_ncm}</span>
          </div>
        ` : '')}
        ${quote.goods_type ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 10px; color: #9CA3AF;">Tipo: </span>
            <span style="font-size: 12px; color: #111827;">${CARGO_TYPE_LABELS[quote.goods_type] || quote.goods_type}</span>
          </div>
        ` : ''}
        ${quote.goods_special ? `
          <div style="margin-bottom: 8px;">
            <span style="background: #FEF3C7; color: #D97706; padding: 2px 8px; border-radius: 4px; font-size: 11px;">Manuseio Especial</span>
          </div>
        ` : ''}
        ${volumesHtml}
      </div>

      <!-- Footer -->
      <div style="position: absolute; bottom: 40px; left: 40px; right: 40px; display: flex; justify-content: space-between; align-items: center; padding-top: 16px; border-top: 1px solid #E5E7EB;">
        <div style="font-size: 10px; color: #9CA3AF;">
          Gerado em ${new Date().toLocaleDateString('pt-BR')}
        </div>
        <div style="font-size: 10px; color: #059669; font-weight: 600;">
          Powered by eximtech.com.br
        </div>
      </div>
    </div>
  `
}

/**
 * Generate PDF using html2canvas + jsPDF
 */
export async function generateQuotePdfBlob(quote: QuoteExportData): Promise<{ url: string; fileName: string }> {
  const fileName = `cotacao_${quote.tx_control || quote.id.slice(0, 8)}.pdf`

  // Load logo as base64 if available
  console.log('[PDF] Loading logo...')
  const logoBase64 = quote.shipper_logo_url ? await imageToBase64(quote.shipper_logo_url) : null
  console.log('[PDF] Logo loaded:', logoBase64 ? 'yes' : 'no')

  // Create temporary container
  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.top = '0'
  container.innerHTML = createPdfHtml(quote, logoBase64)
  document.body.appendChild(container)

  try {
    console.log('[PDF] Capturing with html2canvas...')

    // Capture as canvas
    const canvas = await html2canvas(container.firstElementChild as HTMLElement, {
      scale: 2, // Higher quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    })

    console.log('[PDF] Canvas captured, creating PDF...')

    // Create PDF (A4 size)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    // Calculate dimensions to fit A4
    const imgWidth = 210 // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    // Add image to PDF
    const imgData = canvas.toDataURL('image/png')
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)

    console.log('[PDF] PDF created, generating blob...')

    // Get blob
    const blob = pdf.output('blob')
    const url = URL.createObjectURL(blob)

    console.log('[PDF] Done!')
    return { url, fileName }

  } finally {
    // Cleanup
    document.body.removeChild(container)
  }
}

/**
 * Generate and download PDF directly
 */
export async function generateQuotePdf(quote: QuoteExportData): Promise<void> {
  const { url, fileName } = await generateQuotePdfBlob(quote)

  // Trigger download
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Cleanup
  URL.revokeObjectURL(url)
}
